import { useEffect, useMemo, useRef, useState } from "react";
import OrderPanel from "./OrderPanel.jsx";
import { submitReturn } from "../lib/submitReturn.js";

const COMMENT_MAX = 300;
const MAX_PARCELS = 5;

export default function ReturnForm({ data }) {
  const {
    orders,
    reasons,
    labels,
    paths,
    descrReasonNo,
    defectReasonNo,
    descriptionReasons,
    defectReasons,
    initialOrderNo,
  } = data;

  const formRef = useRef(null);
  const rootRef = useRef(null);

  // The order the customer arrived with opens first; the rest stay closed.
  const [open, setOpen] = useState(() => {
    const match = orders.find((o) => String(o.externalOrderNo) === String(initialOrderNo));
    return new Set([String((match ?? orders[0])?.id)].filter(Boolean));
  });
  const [itemState, setItemState] = useState({});
  const [items, setItems] = useState({});
  const [comment, setComment] = useState("");
  const [parcels, setParcels] = useState("1");
  const [notice, setNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch the line items for each panel the first time it opens.
  useEffect(() => {
    for (const id of open) {
      if (items[id]) continue;
      setItems((prev) => ({ ...prev, [id]: { loading: true } }));
      fetch(paths.orderDetails.replace(":order_id", id), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((json) =>
          setItems((prev) => ({ ...prev, [id]: { lineItems: json.lineItems ?? [] } })),
        )
        .catch(() => setItems((prev) => ({ ...prev, [id]: { error: true } })));
    }
  }, [open, items, paths.orderDetails]);

  const selectedIds = useMemo(
    () =>
      Object.entries(itemState)
        .filter(([, s]) => s.selected)
        .map(([id]) => id),
    [itemState],
  );

  // The parcel count can never exceed the number of articles being sent, and
  // the warehouse caps a single return at five parcels.
  const parcelCap = Math.min(Math.max(selectedIds.length, 1), MAX_PARCELS);
  useEffect(() => {
    if (Number(parcels) > parcelCap) setParcels(String(parcelCap));
  }, [parcelCap, parcels]);

  const extraOptionsFor = (reasonId) => {
    if (String(reasonId) === String(descrReasonNo)) return descriptionReasons;
    if (String(reasonId) === String(defectReasonNo)) return defectReasons;
    return [];
  };

  const patchItem = (id, patch) =>
    setItemState((prev) => ({
      ...prev,
      [id]: { reasonId: "", extraReason: "", detail: "", selected: false, ...prev[id], ...patch },
    }));

  const toggleItem = (id, selected) =>
    patchItem(
      id,
      selected
        ? { selected }
        : { selected, reasonId: "", extraReason: "", detail: "", invalid: false },
    );

  const selectAll = (order, checked) =>
    (items[order.id]?.lineItems ?? [])
      .filter((i) => !i.returnState)
      .forEach((i) => toggleItem(i.id, checked));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    if (selectedIds.length === 0) {
      setNotice(labels.noArticlesSelected);
      return;
    }
    // Every selected article needs a reason; mark the ones that do not have one.
    const missing = selectedIds.filter((id) => !itemState[id].reasonId);
    if (missing.length > 0) {
      setItemState((prev) => {
        const next = { ...prev };
        for (const id of missing) next[id] = { ...next[id], invalid: true };
        return next;
      });
      setNotice(labels.missingReason);
      return;
    }

    setNotice(null);
    setSubmitting(true);
    try {
      await submitReturn(formRef.current, { thankYouPath: paths.thankYou });
    } finally {
      setSubmitting(false);
    }
  };

  if (orders.length === 0) {
    return (
      <div ref={rootRef}>
        <h1>{labels.noOrders}</h1>
        <p>
          <a
            href="#"
            className="button small left"
            onClick={(e) => {
              e.preventDefault();
              history.back();
            }}
          >
            {labels.back}
          </a>
        </p>
      </div>
    );
  }

  const anyPastDate = orders.some((o) => o.pastReturnDate);

  return (
    <div ref={rootRef}>
      {anyPastDate && <h1>{labels.pastReturnDate}</h1>}
      <h1>{labels.choseArticles}</h1>

      <form
        ref={formRef}
        id="new_return_shipment"
        className="form"
        method="post"
        action={paths.returnShipments}
        onSubmit={handleSubmit}
      >
        <input type="hidden" name="authenticity_token" value={data.csrfToken} />

        {orders.map((order) => (
          <OrderPanel
            key={order.id}
            order={order}
            items={items[String(order.id)]}
            open={open.has(String(order.id))}
            onToggleOpen={() =>
              setOpen((prev) => {
                const next = new Set(prev);
                const key = String(order.id);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
              })
            }
            itemState={itemState}
            reasons={reasons}
            extraOptionsFor={extraOptionsFor}
            labels={labels}
            onToggleItem={toggleItem}
            onChangeItem={patchItem}
            onSelectAll={(checked) => selectAll(order, checked)}
          />
        ))}

        <div className="gr-12 bottom-buffer-s dropdown-inline">
          <label className="gr-7 gr-9@mobile" htmlFor="parcel_count">
            {labels.parcelCount}
          </label>
          <span className="gr-5">
            <select
              id="parcel_count"
              className="form-control"
              required
              name="returns_return_shipment[parcel_count]"
              value={parcels}
              onChange={(e) => setParcels(e.target.value)}
            >
              {Array.from({ length: parcelCap }, (_, i) => String(i + 1)).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </span>
        </div>

        <div className="gr-12 bottom-buffer-s dropdown-inline">
          <label className="gr-7" htmlFor="customer_comment">
            {labels.customerComment}
          </label>
          <span className="gr-5">
            <textarea
              id="customer_comment"
              className="customer_message"
              rows="5"
              style={{ height: "120px" }}
              maxLength={COMMENT_MAX}
              name="returns_return_shipment[customer_comment]"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <span className="countdown_text">{labels.countdownText}</span>
            <span className="countdown">{COMMENT_MAX - comment.length}</span>
          </span>
        </div>

        {notice && (
          <div className="gr-12 bottom-buffer-m" id="flash_notice">
            {notice}
          </div>
        )}

        <div className="gr-12 bottom-buffer-m">
          <button type="submit" className="button-primary" id="print_label" disabled={submitting}>
            {submitting ? labels.pleaseWait : labels.printLabels}
          </button>
        </div>
      </form>
    </div>
  );
}
